import { useState, useMemo, useCallback } from 'react';
import { getQueryParam, getNumericQueryParam } from '../utils';

/**
 * Parameters for useAccountLinking hook
 */
export interface UseAccountLinkingParams {
    /**
     * Custom function to get search params. Useful for testing.
     * Defaults to using window.location.search.
     */
    getSearchParams?: (() => URLSearchParams) | undefined;
    /**
     * Custom function to get Auth0 domain. Useful for testing.
     * Defaults to using import.meta.env.VITE_AUTH0_DOMAIN.
     */
    getDomain?: (() => string) | undefined;
}

/**
 * Result of useAccountLinking hook
 */
export interface UseAccountLinkingResult {
    /**
     * Whether a consent operation is in progress
     */
    isLoading: boolean;
    /**
     * Error state. Can be boolean (true = expired/missing state) or string (error message)
     */
    error: boolean | string;
    /**
     * The state parameter from the URL
     */
    state: string | null;
    /**
     * The expiration timestamp from the URL
     */
    expire: number | null;
    /**
     * Whether the consent link has expired
     */
    isExpired: boolean;
    /**
     * Function to handle user consent
     * @param hasConsent - Whether the user consents to account linking
     */
    handleConsent: (hasConsent: boolean) => void;
}

/**
 * Default function to get search params from window.location
 */
const defaultGetSearchParams = (): URLSearchParams => {
    if (typeof window === 'undefined') {
        return new URLSearchParams('');
    }
    return new URLSearchParams(window.location.search);
};

/**
 * Default function to get Auth0 domain from environment
 */
const defaultGetDomain = (): string => import.meta.env.VITE_AUTH0_DOMAIN ?? '';

/**
 * Custom hook to handle account linking consent logic.
 * This hook extracts the business logic from the LinkUserConsent component,
 * making it more testable and reusable.
 *
 * @param params - Optional parameters for dependency injection
 * @returns Object with consent handling state and functions
 *
 * @example
 * ```tsx
 * const { loading, error, isExpired, handleConsent } = useAccountLinking();
 *
 * return (
 *   <div>
 *     {error ? (
 *       <p>Link has expired</p>
 *     ) : (
 *       <button onClick={() => handleConsent(true)}>
 *         Accept
 *       </button>
 *     )}
 *   </div>
 * );
 * ```
 */
export const useAccountLinking = (
    params: UseAccountLinkingParams = {},
): UseAccountLinkingResult => {
    const { getSearchParams, getDomain } = params;

    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState<boolean | string>(false);

    // Get and parse query parameters
    const searchParams = useMemo(() => {
        const fn = getSearchParams ?? defaultGetSearchParams;
        return fn();
    }, [getSearchParams]);
    const state = useMemo(
        () => getQueryParam('state', searchParams.toString()),
        [searchParams],
    );
    const expire = useMemo(
        () => getNumericQueryParam('expire', searchParams.toString()),
        [searchParams],
    );

    // Check if the link has expired
    const isExpired = useMemo(() => {
        if (expire === null) {
            return true;
        }
        return isNaN(expire) || expire < Date.now();
    }, [expire]);

    // Initialize error state based on required parameters
    useMemo(() => {
        if (!state || isExpired) {
            setError(true);
        }
    }, [state, isExpired]);

    /**
     * Handles the user's consent decision.
     * Redirects back to Auth0 with the consent result.
     *
     * @param consent - Whether the user consents to account linking
     */
    const handleConsent = useCallback(
        async (consent: boolean) => {
            if (isExpired) {
                setError(true);
                return;
            }

            if (!state) {
                setError('Missing state parameter');
                return;
            }

            setLoading(true);
            try {
                const domain = (getDomain ?? defaultGetDomain)();
                if (!domain) {
                    throw new Error('Auth0 domain is not configured');
                }

                const consentValue = consent ? 'true' : 'false';
                window.location.href = `https://${domain}/continue?state=${encodeURIComponent(state)}&consent=${consentValue}`;
            } catch (err) {
                const errorMessage =
                    err instanceof Error
                        ? err.message
                        : 'Failed to process consent. Please try again.';
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        },
        [isExpired, state, getDomain],
    );

    return {
        isLoading,
        error,
        state,
        expire,
        isExpired,
        handleConsent,
    };
};

export default useAccountLinking;
